import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  Input,
  input,
  OnInit,
  output,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { MatError } from '@angular/material/select';
import type { DateRangeElement } from '@iot-platform/iot-platform-utils';
import { TranslatePipe } from '@ngx-translate/core';
import moment from 'moment';
import { Subject } from 'rxjs';
import { FormControlType, FormField, FormGroupComponent } from '../forms';
import { DateRange } from './date-range.model';

interface DateRangeForm {
  startDate: FormControl<Date | null>;
  endDate: FormControl<Date | null>;
}

@Component({
  imports: [FlexLayoutModule, TranslatePipe, FormGroupComponent, MatError],
  selector: 'iot-platform-ui-date-range',
  templateUrl: './date-range.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./date-range.component.scss']
})
export class DateRangeComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  readonly injector = inject(Injector);

  readonly placeholderFrom = input<string>('DATE_RANGE.PLACEHOLDER_FROM');
  readonly placeholderTo = input<string>('DATE_RANGE.PLACEHOLDER_TO');
  readonly placeholderHours = input<string>('DATE_RANGE.PLACEHOLDER_HOURS');
  readonly placeholderMinutes = input<string>('DATE_RANGE.PLACEHOLDER_MINUTES');
  readonly placeholderClear = input<string>('DATE_RANGE.CLEAR');
  readonly toggleIcon = input<string>();
  readonly required = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly dateFormatterPattern = input<string>('');
  readonly diffInMonths = input<number>(0);

  @Input() clearDateRangeFilters$ = new Subject<void>();
  @Input() validateForm$ = new Subject<void>();

  readonly dateRangeChange = output<DateRange>();
  readonly dateRangeReady = output<FormGroup<DateRangeForm>>();
  readonly dateRangeCleared = output<DateRange>();

  form!: FormGroup<DateRangeForm>;

  readonly startFields: WritableSignal<Partial<FormField>[]> = signal([]);
  readonly endFields: WritableSignal<Partial<FormField>[]> = signal([]);
  readonly startDate: WritableSignal<DateRangeElement | null> = signal(null);
  readonly endDate: WritableSignal<DateRangeElement | null> = signal(null);

  private readonly DEFAULT_END_HOURS = 23;
  private readonly DEFAULT_END_MINUTES = 59;
  private readonly DEFAULT_END_SECONDS = 0;
  private readonly DEFAULT_START_TIME = 0;

  constructor() {
    effect(() => {
      const startDate = this.startDate();
      const endDate = this.endDate();
      this.dateRangeChange.emit({
        startDate,
        endDate
      });
    });
  }
  get startDateControl(): FormControl<Date | null> {
    return this.form.controls.startDate;
  }

  get endDateControl(): FormControl<Date | null> {
    return this.form.controls.endDate;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupEffects();

    this.dateRangeReady.emit(this.form);

    this.clearDateRangeFilters$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resetForm();
    });

    this.validateForm$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.validateAllFields();
    });

    this.initializeFormFields();
  }

  validateAllFields(): void {
    Object.values(this.form.controls).forEach((control) => {
      control.markAsTouched({ onlySelf: true });
      control.updateValueAndValidity();
    });
  }

  resetForm(): void {
    this.form.reset(
      {
        startDate: null,
        endDate: null
      },
      { emitEvent: true }
    );
    this.startDate.set(null);
    this.endDate.set(null);
  }

  resetStartDate(): void {
    this.form.patchValue({
      startDate: null
    });
    this.startDate.set(null);
    this.dateRangeCleared.emit({ startDate: null, endDate: this.endDate() });
  }

  resetEndDate(): void {
    this.form.patchValue({
      endDate: null
    });
    this.endDate.set(null);
    this.dateRangeCleared.emit({ startDate: this.startDate(), endDate: null });
  }

  readonly dateRangeDiffInMonthsValidator: ValidatorFn = (): {
    [key: string]: any;
  } | null => {
    const from = this.startDate();
    const to = this.endDate();

    if (!from?.date || !to?.date) return null;

    const diffInMonths = moment.duration(moment(to.date).diff(moment(from.date))).asMonths();
    const isInvalid = diffInMonths > this.diffInMonths();

    return isInvalid ? { invalidRange: { from: from.date, to: to.date } } : null;
  };

  private initializeForm(): void {
    this.form = new FormGroup<DateRangeForm>({
      startDate: new FormControl<Date | null>(null),
      endDate: new FormControl<Date | null>(null)
    });
    if (this.diffInMonths() > 0) {
      this.form.setValidators([this.dateRangeDiffInMonthsValidator]);
    }
  }

  private setupEffects(): void {
    effect(
      () => {
        const diffInMonths = this.diffInMonths();
        const validators = diffInMonths > 0 ? [this.dateRangeDiffInMonthsValidator] : [];
        this.form.setValidators(validators);
        this.form.updateValueAndValidity({ emitEvent: false });
      },
      { injector: this.injector }
    );
  }

  private initializeFormFields(): void {
    this.startFields.set([
      {
        type: FormControlType.DATE_TIME_PICKER,
        name: signal('startDate'),
        label: this.placeholderFrom,
        disabled: this.disabled,
        required: this.required,
        toggleIcon: this.toggleIcon,
        placeholderHours: this.placeholderHours,
        placeholderMinutes: this.placeholderMinutes,
        clearLabel: this.placeholderClear,
        max: computed(() => this.endDate()?.date),
        initialHours: signal(this.DEFAULT_START_TIME),
        initialMinutes: signal(this.DEFAULT_START_TIME),
        initialSeconds: signal(this.DEFAULT_START_TIME),
        onClear: () => this.resetStartDate(),
        selectionChange: (event) => this.startDate.set(event?.value)
      }
    ]);

    this.endFields.set([
      {
        type: FormControlType.DATE_TIME_PICKER,
        name: signal('endDate'),
        label: this.placeholderTo,
        disabled: this.disabled,
        required: this.required,
        toggleIcon: this.toggleIcon,
        placeholderHours: this.placeholderHours,
        placeholderMinutes: this.placeholderMinutes,
        clearLabel: this.placeholderClear,
        initialHours: signal(this.DEFAULT_END_HOURS),
        initialMinutes: signal(this.DEFAULT_END_MINUTES),
        initialSeconds: signal(this.DEFAULT_END_SECONDS),
        min: computed(() => this.startDate()?.date),
        onClear: () => this.resetEndDate(),
        selectionChange: (event) => this.endDate.set(event?.value)
      }
    ]);
  }
}
